import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, Plus, Search, Star, SlidersHorizontal, ChevronDown, Tag, Percent, Clock, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { useWishlist } from '../contexts/WishlistContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ProductBanner } from '../components/CampaignBanner';
import { calculateSalePrice } from '../utils/calculateSalePrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const { addItem } = useCart();
  const { formatPrice, convertPrice, currency } = useCurrency();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  // Track referral/affiliate codes from URL parameters
  useEffect(() => {
    const refCode = searchParams.get('ref');
    const affCode = searchParams.get('aff');
    const campaignCode = searchParams.get('campaign');
    
    if (refCode) {
      localStorage.setItem('referral_code', refCode.toUpperCase());
      console.log('Referral code tracked:', refCode);
    }
    if (affCode) {
      localStorage.setItem('affiliate_code', affCode.toUpperCase());
      console.log('Affiliate code tracked:', affCode);
    }
    if (campaignCode) {
      localStorage.setItem('campaign_code', campaignCode.toUpperCase());
      console.log('Campaign code tracked:', campaignCode);
    }
  }, [searchParams]);

  const handleToggleWishlist = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (isInWishlist(product.id)) {
      await removeFromWishlist(product.id, product.name);
    } else {
      await addToWishlist(product.id, product.name);
    }
  };

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [priceRange, setPriceRange] = useState('all');
  const [size, setSize] = useState('all');
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await axios.post(`${API_URL}/api/seed`);
        const [productsRes, promosRes] = await Promise.all([
          axios.get(`${API_URL}/api/products`),
          axios.get(`${API_URL}/api/promotions/active`).catch(() => ({ data: [] }))
        ]);
        setProducts(productsRes.data);
        setFilteredProducts(productsRes.data);
        setPromotions(promosRes.data || []);
      } catch (e) {
        console.error('Error fetching products:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Apply filters
  useEffect(() => {
    let result = [...products];

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(query) || 
        p.description.toLowerCase().includes(query)
      );
    }

    // Category
    if (category !== 'all') {
      result = result.filter(p => p.category === category);
    }

    // Price range
    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(Number);
      result = result.filter(p => {
        if (max) return p.price >= min && p.price <= max;
        return p.price >= min;
      });
    }

    // Size
    if (size !== 'all') {
      result = result.filter(p => p.size === size);
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    setFilteredProducts(result);
  }, [products, searchQuery, category, priceRange, size, sortBy]);

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'freeze-dryer', label: 'Freeze Dryers' },
    { value: 'accessory', label: 'Accessories' }
  ];

  const priceRanges = [
    { value: 'all', label: 'All Prices' },
    { value: '0-100', label: 'Under $100' },
    { value: '100-1000', label: '$100 - $1,000' },
    { value: '1000-2500', label: '$1,000 - $2,500' },
    { value: '2500-5000', label: '$2,500 - $5,000' },
    { value: '5000-', label: '$5,000+' }
  ];

  const sizes = [
    { value: 'all', label: 'All Sizes' },
    { value: 'Small', label: 'Small / Compact' },
    { value: 'Medium', label: 'Medium / Standard' },
    { value: 'XL', label: 'XL / Commercial' }
  ];

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'name', label: 'Name' }
  ];

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      <div className="pt-44 pb-16 section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold text-[#0a0203] mb-4" style={{  }}>
              Our Products
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Explore our range of premium freeze dryers and accessories designed for home food preservation.
            </p>
          </div>

          {/* Active Promotions Banner */}
          {promotions.length > 0 && (
            <div className="mb-8 animate-fade-in">
              <div className="bg-gradient-to-r from-[#003456] to-[#001d30] rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#fdc31c] rounded-full blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="h-5 w-5 text-[#fdc31c]" />
                    <span className="text-[#fdc31c] font-semibold text-sm uppercase tracking-wide">Current Offers</span>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {promotions.map((promo, i) => (
                      <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-[#fdc31c] rounded-lg flex items-center justify-center">
                            <Percent className="h-5 w-5 text-[#0a0203]" />
                          </div>
                          <div>
                            <h3 className="text-white font-bold">{promo.code}</h3>
                            <p className="text-white/60 text-xs">
                              {promo.discount_type === 'percentage' 
                                ? `${promo.discount_value}% OFF` 
                                : `$${promo.discount_value} OFF`}
                            </p>
                          </div>
                        </div>
                        <p className="text-white/80 text-sm">{promo.description}</p>
                        {promo.valid_until && (
                          <div className="flex items-center gap-1 mt-2 text-white/50 text-xs">
                            <Clock className="h-3 w-3" />
                            Expires: {new Date(promo.valid_until).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Campaign Banner */}
          <ProductBanner className="mb-8 animate-fade-in" />

          {/* Search and Filters Bar */}
          <div className="bg-white rounded-2xl p-4 mb-8 shadow-sm">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="pl-12 py-3 rounded-xl"
                  data-testid="search-input"
                />
              </div>

              {/* Quick Filters */}
              <div className="flex items-center gap-3 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => setCategory(cat.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      category === cat.value
                        ? 'bg-[#003456] text-white'
                        : 'bg-gray-100 text-[#0a0203] hover:bg-gray-200'
                    }`}
                    data-testid={`filter-${cat.value}`}
                  >
                    {cat.label}
                  </button>
                ))}

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    showFilters ? 'bg-[#003456] text-white' : 'bg-gray-100 text-[#0a0203] hover:bg-gray-200'
                  }`}
                  data-testid="toggle-filters"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Filters
                </button>
              </div>
            </div>

            {/* Extended Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4 animate-slide-up">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Price Range</label>
                  <select
                    value={priceRange}
                    onChange={(e) => setPriceRange(e.target.value)}
                    className="w-full p-2 rounded-lg border border-gray-200 text-sm"
                    data-testid="filter-price"
                  >
                    {priceRanges.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Size</label>
                  <select
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="w-full p-2 rounded-lg border border-gray-200 text-sm"
                    data-testid="filter-size"
                  >
                    {sizes.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full p-2 rounded-lg border border-gray-200 text-sm"
                    data-testid="filter-sort"
                  >
                    {sortOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setCategory('all');
                      setPriceRange('all');
                      setSize('all');
                      setSortBy('featured');
                    }}
                    className="text-sm text-[#003456] hover:underline"
                    data-testid="clear-filters"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-[#0a0203]">{filteredProducts.length}</span> products
            </p>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003456] border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
              {filteredProducts.map((product) => (
                <Link 
                  key={product.id} 
                  to={`/products/${product.id}`} 
                  className="group"
                  data-testid={`product-${product.id}`}
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover">
                    <div className="aspect-square bg-gray-100 overflow-hidden relative">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {!product.in_stock && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="bg-[#fdc31c] text-white px-4 py-2 rounded-full font-medium">
                            Out of Stock
                          </span>
                        </div>
                      )}
                      
                      {/* Badges Container */}
                      <div className="absolute top-3 left-3 flex flex-col gap-2">
                        {product.size && (
                          <span className="bg-[#0a0203] text-white px-2 py-1 rounded-full text-xs font-medium">
                            {product.size}
                          </span>
                        )}
                        {calculateSalePrice(product, currency).isOnSale && (
                          <span className="text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse" style={{ backgroundColor: '#10b981' }}>
                            SALE
                          </span>
                        )}
                        {product.is_affiliate_product && (
                          <span className="bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                            {product.affiliate_badge || 'Partner'}
                          </span>
                        )}
                        {/* Low Stock Badge */}
                        {product.in_stock && product.stock_quantity !== null && product.stock_quantity !== undefined && product.stock_quantity <= (product.low_stock_threshold || 5) && product.stock_quantity > 0 && (
                          <span className="bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                            Only {product.stock_quantity} left
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={(e) => handleAddToCart(e, product)}
                        disabled={!product.in_stock}
                        className="absolute bottom-3 right-3 bg-[#003456] text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-[#001d30] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                      
                      {/* Wishlist Button */}
                      <button
                        onClick={(e) => handleToggleWishlist(e, product)}
                        className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 shadow-sm ${
                          isInWishlist(product.id) 
                            ? 'bg-red-500 text-white' 
                            : 'bg-white/90 text-gray-400 hover:text-red-500'
                        }`}
                        data-testid={`wishlist-${product.id}`}
                      >
                        <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? 'fill-current' : ''}`} />
                      </button>
                    </div>
                    
                    {/* Affiliate Product Notification */}
                    {product.is_affiliate_product && product.affiliate_message && (
                      <div className="bg-purple-50 border-l-4 border-purple-500 px-4 py-2">
                        <p className="text-purple-700 text-xs font-medium">
                          {product.affiliate_message}
                        </p>
                      </div>
                    )}
                    
                    <div className="p-5">
                      {/* Rating */}
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-3.5 w-3.5 text-yellow-400 fill-current" />
                        ))}
                        <span className="text-xs text-gray-500 ml-1">(127)</span>
                      </div>
                      
                      <span className="text-xs text-[#003456] font-medium uppercase tracking-wider">
                        {product.category}
                      </span>
                      <h3 className="font-semibold text-[#0a0203] mt-1 line-clamp-1" style={{  }}>
                        {product.name}
                      </h3>
                      <div className="flex items-baseline gap-2 mt-2">
                        {(() => {
                          const saleInfo = calculateSalePrice(product, currency);
                          if (saleInfo.isOnSale && saleInfo.effectivePrice) {
                            return (
                              <>
                                <span className="text-xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>
                                  {formatPrice(saleInfo.effectivePrice)}
                                </span>
                                <span className="text-sm text-gray-400 line-through" style={{ fontFamily: 'JetBrains Mono' }}>
                                  {formatPrice(product.price)}
                                </span>
                              </>
                            );
                          }
                          return (
                            <span className="text-xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatPrice(product.price)}
                            </span>
                          );
                        })()}
                        {product.category === 'freeze-dryer' && !calculateSalePrice(product, currency).isOnSale && (
                          <span className="text-xs text-gray-500">
                            or ${Math.round(convertPrice(product.price) / 24)}/mo
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {filteredProducts.length === 0 && !loading && (
            <div className="text-center py-20 bg-white rounded-2xl">
              <Search className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-[#0a0203] mb-2">No products found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your search or filter criteria</p>
              <Button
                onClick={() => {
                  setSearchQuery('');
                  setCategory('all');
                  setPriceRange('all');
                  setSize('all');
                }}
                variant="outline"
                className="border-[#003456] text-[#003456]"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Products;
